import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { inputBorderColor } from 'Styles/helpers';
import Label from '../label';
import HelperText from '../helper-text';

const Input = ({
  type,
  name,
  value,
  label,
  errors,
  resetSearch,
  validation,
  hideLabel,
  required,
  placeholder,
  helperText,
  isDark,
  styles,
  disabled,
  ...rest
}) => {

  const id = `${type}-${name}`;

  return (
    <InputWrap validation={validation} hideLabel={hideLabel}>

      {label &&
        <Label htmlFor={id} hideLabel={hideLabel}>{label}</Label>
      }

      <StyledInput
        disabled={disabled}
        type={type}
        name={name}
        value={value}
        errors={errors}
        id={id}
        placeholder={`${placeholder}${required ? '*' : ''}`}
        required={required}
        isDark={isDark}
        styles={styles}
        {...rest}
      />

      <HelperText>
        {helperText && !errors &&
          <HelperText.Text>{helperText}</HelperText.Text>
        }
        {errors &&
          <HelperText.Error>{errors}</HelperText.Error>
        }
      </HelperText>
    </InputWrap>
  );
};

const InputWrap = styled.div`
  position: relative;
  padding-bottom: ${props => props.validation ? '2rem' : '1rem'};
  width: 100%;
  font-size: 1rem;
`;

const StyledInput = styled.input`
  opacity: ${props => props.disabled ? '0.5' : 1};
  line-height: 1.5rem;
  padding: .5rem 0;
  width: 100%;
  background-color: transparent;
  border-radius: 0;
  border: 0;
  border-bottom: 2px solid ${props => inputBorderColor(props)};
  font-size: inherit;
  font-family: inherit;
  outline: none;
  transition: border-color .2s ease;
  appearance: none;

  &:focus {
    border-color: ${props => props.theme.blue};
  }

  &[type="search"]::-webkit-search-decoration,
  &[type="search"]::-webkit-search-cancel-button,
  &[type="search"]::-webkit-search-results-button,
  &[type="search"]::-webkit-search-results-decoration,
  &[type="date"]::-webkit-clear-button,
  &[type="date"]::-webkit-inner-spin-button,
  &[type="date"]::-webkit-calendar-picker-indicator {
    display: none;
  }

  &:-webkit-autofill,
  &:-internal-autofill-selected {
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-box-shadow: 0 0 0 1000px ${props => props.isDark ? props.theme.black : '#fff'} inset !important;
    -webkit-text-fill-color: ${props => props.isDark ? '#fff' : props.theme.black} !important;
    /* stylelint-enable */
  }

  &:required,
  &:invalid {
    box-shadow: none;
  }

  &::placeholder {
    color: currentColor;
    opacity: .5;
  }

  ${props => props.styles && css`
    ${props.styles}
  `}
`;

Input.propTypes = {
  label: PropTypes.string,
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  errors: PropTypes.string,
  validation: PropTypes.bool,
  hideLabel: PropTypes.bool,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  isDark: PropTypes.bool,
};

Input.defaultProps = {
  label: undefined,
  validation: true,
  hideLabel: true,
};

export default Input;
